import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  message,
  Upload,
  Progress,
  Switch,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiService } from "@/services/api";
import { SelecteurLangue } from "@/components/content/SelecteurLangue";
import {
  SelecteurAgeMinimum,
  SelecteurDescripteurs,
} from "@/components/content/SelecteurClassification";
import { Content, ContentType, Genre, Cast } from "@/types";
import { CastingSelector } from "@/components/content/CastingSelector";
import { useProcessingStatus } from "@/hooks/useProcessingStatus";
import { ProcessingStatusBadge } from "@/components/media/ProcessingStatusBadge";

const { TextArea } = Input;

export const ContentCreate: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState<ContentType[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [createdContent, setCreatedContent] = useState<Content | null>(null);
  const [videoUploaded, setVideoUploaded] = useState(false);
  const [trailerUploaded, setTrailerUploaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingTrailer, setUploadingTrailer] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [trailerUploadProgress, setTrailerUploadProgress] = useState(0);
  const [videoFileList, setVideoFileList] = useState<any[]>([]);
  const [trailerFileList, setTrailerFileList] = useState<any[]>([]);
  const [posterFileList, setPosterFileList] = useState<any[]>([]);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [posterUploaded, setPosterUploaded] = useState(false);

  // Polling du statut de transcodage après upload
  const { data: processingStatus } = useProcessingStatus(
    createdContent?.id ?? null,
    videoUploaded || trailerUploaded
  );

  const selectedTypeId = Form.useWatch("typeId", form);

  useEffect(() => {
    (async () => {
      try {
        const [typesRes, genresRes] = await Promise.all([
          apiService.getContentTypes(),
          apiService.getGenres(),
        ]);
        setTypes(typesRes as any);
        setGenres(genresRes as any);
      } catch {
        message.error("Impossible de charger types/genres");
      }
    })();
  }, []);

  const selectedType = useMemo(
    () => types.find((t) => String(t.id) === String(selectedTypeId)),
    [types, selectedTypeId]
  );

  const onSubmit = async (values: any) => {
    try {
      setLoading(true);

      // Validation côté client pour éviter les erreurs de longueur
      if (values.title && values.title.length > 255) {
        message.error("Le titre ne peut pas dépasser 255 caractères");
        setLoading(false);
        return;
      }

      if (values.description && values.description.length > 2000) {
        message.error("La description ne peut pas dépasser 2000 caractères");
        setLoading(false);
        return;
      }

      if (values.originalLanguage && values.originalLanguage.length > 10) {
        message.error("Le code de langue ne peut pas dépasser 10 caractères");
        setLoading(false);
        return;
      }

      // Validation des tags
      if (values.tags && values.tags.length > 0) {
        const longTags = values.tags.filter(
          (tag: string) => tag && tag.length > 50
        );
        if (longTags.length > 0) {
          message.error(
            "Les tags ne peuvent pas dépasser 50 caractères chacun"
          );
          setLoading(false);
          return;
        }
      }

      // Structure attendue par le backend ContentRequestDTO
      const payload: any = {
        title: values.title?.trim(),
        description: values.description?.trim(),
        releaseDate: values.releaseDate
          ? dayjs(values.releaseDate).format("YYYY-MM-DD")
          : undefined,
        status: values.status || "DRAFT",
        typeId: values.typeId, // Backend attend typeId (Long) - use form value directly
        genreIds: values.genres || [], // Backend attend genreIds (List<Long>)
        tags:
          values.tags
            ?.map((tag: string) => tag.trim())
            .filter((tag: string) => tag.length > 0) || [],
        // posterUrl sera défini par l'upload d'affiche après création
        // Nouveaux champs multilangue - seulement si définis
        ...(values.originalLanguage && {
          originalLanguage: values.originalLanguage,
        }),
        hasSubtitles: Boolean(values.hasSubtitles),
        hasMultiAudio: Boolean(values.hasMultiAudio),
        // Classification : n'envoyer que si elle est renseignée. Le service
        // ne remplace la valeur en base que lorsque la requête la porte.
        ...(values.ageRating !== undefined &&
          values.ageRating !== null && { ageRating: values.ageRating }),
        ...(values.contentDescriptors && {
          contentDescriptors: values.contentDescriptors,
        }),
      };

      // Ajouter les données spécifiques au type de contenu
      if (
        selectedType &&
        selectedType.title?.toLowerCase() === "movie" &&
        values.movie
      ) {
        // Validation des valeurs numériques
        const duration = values.movie.duration
          ? Number(values.movie.duration)
          : undefined;
        const budget = values.movie.budget
          ? Number(values.movie.budget)
          : undefined;
        const boxOffice = values.movie.boxOffice
          ? Number(values.movie.boxOffice)
          : undefined;

        if (duration && (isNaN(duration) || duration <= 0)) {
          message.error("La durée doit être un nombre positif");
          setLoading(false);
          return;
        }

        if (budget && (isNaN(budget) || budget < 0)) {
          message.error("Le budget doit être un nombre positif ou zéro");
          setLoading(false);
          return;
        }

        if (boxOffice && (isNaN(boxOffice) || boxOffice < 0)) {
          message.error("Le box office doit être un nombre positif ou zéro");
          setLoading(false);
          return;
        }

        payload.movie = {
          duration: duration,
          director: values.movie.director?.trim() || undefined,
          budget: budget,
          boxOffice: boxOffice,
          videoUrl: undefined, // Sera rempli après l'upload
          timeline: undefined, // Sera rempli après l'upload
        };
      }

      if (
        selectedType &&
        selectedType.title?.toLowerCase() === "series" &&
        values.series
      ) {
        // Validation des valeurs numériques pour les séries
        const totalSeasons = values.series.totalSeasons
          ? Number(values.series.totalSeasons)
          : undefined;
        const totalEpisodes = values.series.totalEpisodes
          ? Number(values.series.totalEpisodes)
          : undefined;
        const averageEpisodeDuration = values.series.averageEpisodeDuration
          ? Number(values.series.averageEpisodeDuration)
          : undefined;

        if (totalSeasons && (isNaN(totalSeasons) || totalSeasons <= 0)) {
          message.error("Le nombre de saisons doit être un nombre positif");
          setLoading(false);
          return;
        }

        if (totalEpisodes && (isNaN(totalEpisodes) || totalEpisodes <= 0)) {
          message.error("Le nombre d'épisodes doit être un nombre positif");
          setLoading(false);
          return;
        }

        if (
          averageEpisodeDuration &&
          (isNaN(averageEpisodeDuration) || averageEpisodeDuration <= 0)
        ) {
          message.error(
            "La durée moyenne des épisodes doit être un nombre positif"
          );
          setLoading(false);
          return;
        }

        payload.series = {
          totalSeasons: totalSeasons,
          totalEpisodes: totalEpisodes,
          averageEpisodeDuration: averageEpisodeDuration,
        };
      }

      // Validation finale du payload
      console.log("Payload à envoyer:", JSON.stringify(payload, null, 2));

      // Vérifier que tous les champs obligatoires sont présents
      if (!payload.title || !payload.typeId) {
        message.error("Le titre et le type sont obligatoires");
        setLoading(false);
        return;
      }

      const created = await apiService.createContent(payload);
      setCreatedContent(created);
      message.success(
        "Contenu créé. Vous pouvez maintenant uploader la vidéo."
      );
    } catch (e: any) {
      console.error("Erreur création contenu:", e);
      message.error(e?.response?.data?.message || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  const videoUploadProps = {
    beforeUpload: (file: File) => {
      setVideoFileList([file]);
      return false; // prevent auto upload
    },
    onRemove: () => {
      setVideoFileList([]);
    },
    fileList: videoFileList as any,
  };

  const trailerUploadProps = {
    beforeUpload: (file: File) => {
      setTrailerFileList([file]);
      return false; // prevent auto upload
    },
    onRemove: () => {
      setTrailerFileList([]);
    },
    fileList: trailerFileList as any,
  };

  const onUploadVideo = async () => {
    if (!createdContent) {
      message.warning("Créez d'abord le contenu");
      return;
    }
    if (videoFileList.length === 0) {
      message.warning("Sélectionnez un fichier vidéo");
      return;
    }
    try {
      setUploading(true);
      setVideoUploadProgress(0);

      console.log("Tentative d'upload vidéo complète pour le contenu ID:", createdContent.id);
      console.log("Fichier sélectionné:", videoFileList[0].name);
      console.log("Type de contenu:", selectedType?.title || "MOVIE");

      const result = await apiService.uploadMedia(
        videoFileList[0] as File,
        (selectedType?.title || "MOVIE").toUpperCase(),
        createdContent.id,
        (progress) => {
          setVideoUploadProgress(progress);
        }
      );

      setVideoUploadProgress(100);
      setVideoUploaded(true);
      console.log("Résultat de l'upload:", result);
      message.success("Upload de la vidéo complète démarré avec succès. Le transcodage va commencer.");

      // Réinitialiser la liste des fichiers après upload réussi
      setVideoFileList([]);
    } catch (e: any) {
      console.error("Erreur détaillée lors de l'upload:", e);

      // Gestion d'erreurs plus détaillée
      if (e.response?.status === 404) {
        message.error(
          "Endpoint d'upload non trouvé. Vérifiez la configuration du backend."
        );
      } else if (e.response?.status === 500) {
        message.error(
          "Erreur serveur lors de l'upload. Vérifiez les logs du backend."
        );
      } else if (e.response?.data?.message) {
        message.error(`Erreur: ${e.response.data.message}`);
      } else {
        message.error(`Échec de l'upload: ${e.message}`);
      }
    } finally {
      setUploading(false);
      setVideoUploadProgress(0);
    }
  };

  const onUploadTrailer = async () => {
    if (!createdContent) {
      message.warning("Créez d'abord le contenu");
      return;
    }
    if (trailerFileList.length === 0) {
      message.warning("Sélectionnez un fichier de bande d'annonce");
      return;
    }
    try {
      setUploadingTrailer(true);
      setTrailerUploadProgress(0);

      console.log("Tentative d'upload bande d'annonce pour le contenu ID:", createdContent.id);
      console.log("Fichier sélectionné:", trailerFileList[0].name);

      const result = await apiService.uploadTrailer(
        createdContent.id,
        trailerFileList[0] as File,
        (progress) => {
          setTrailerUploadProgress(progress);
        }
      );

      setTrailerUploadProgress(100);
      setTrailerUploaded(true);
      console.log("Résultat de l'upload:", result);
      message.success("Upload de la bande d'annonce démarré avec succès. Le transcodage va commencer.");

      // Réinitialiser la liste des fichiers après upload réussi
      setTrailerFileList([]);
    } catch (e: any) {
      console.error("Erreur détaillée lors de l'upload du trailer:", e);

      // Gestion d'erreurs plus détaillée
      if (e.response?.status === 404) {
        message.error(
          "Endpoint d'upload du trailer non trouvé. Vérifiez la configuration du backend."
        );
      } else if (e.response?.status === 500) {
        message.error(
          "Erreur serveur lors de l'upload. Vérifiez les logs du backend."
        );
      } else if (e.response?.data?.message) {
        message.error(`Erreur: ${e.response.data.message}`);
      } else {
        message.error(`Échec de l'upload: ${e.message}`);
      }
    } finally {
      setUploadingTrailer(false);
      setTrailerUploadProgress(0);
    }
  };

  const onUploadPoster = async () => {
    if (!createdContent) {
      message.warning("Créez d'abord le contenu");
      return;
    }
    if (posterFileList.length === 0) {
      message.warning("Sélectionnez une image pour l'affiche");
      return;
    }
    try {
      setUploadingPoster(true);

      const result = await apiService.uploadPoster(
        createdContent.id,
        posterFileList[0] as File,
      );

      setPosterUploaded(true);
      console.log("Résultat de l'upload poster:", result);
      message.success("Affiche uploadée avec succès");

      setPosterFileList([]);
    } catch (e: any) {
      console.error("Erreur lors de l'upload de l'affiche:", e);
      message.error(`Échec de l'upload de l'affiche: ${e.message}`);
    } finally {
      setUploadingPoster(false);
    }
  };

  return (
    <div>
      <Card title="Créer un contenu" style={{ marginBottom: 16 }}>
        <Form layout="vertical" form={form} onFinish={onSubmit}>
          <Form.Item
            label="Titre"
            name="title"
            rules={[
              { required: true, message: "Le titre est requis" },
              {
                max: 255,
                message: "Le titre ne peut pas dépasser 255 caractères",
              },
            ]}
          >
            <Input placeholder="Titre du contenu" maxLength={255} showCount />
          </Form.Item>
          <Form.Item
            label="Description"
            name="description"
            rules={[
              {
                max: 2000,
                message: "La description ne peut pas dépasser 2000 caractères",
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Description"
              maxLength={2000}
              showCount
            />
          </Form.Item>
          <Form.Item label="Date de sortie" name="releaseDate">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Statut" name="status" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "PUBLISHED", label: "Publié" },
                { value: "DRAFT", label: "Brouillon" },
                { value: "PENDING", label: "En attente" },
                { value: "ARCHIVED", label: "Archivé" },
              ]}
            />
          </Form.Item>
          <Form.Item label="Type" name="typeId" rules={[{ required: true }]}>
            <Select
              options={types.map((t) => ({ value: t.id, label: t.title }))}
              placeholder="Sélectionner un type"
            />
          </Form.Item>
          <Form.Item label="Genres" name="genres">
            <Select
              mode="multiple"
              options={genres.map((g) => ({ value: g.id, label: g.title }))}
              placeholder="Sélectionner des genres"
            />
          </Form.Item>

          <Form.Item
            label="Langue originale"
            name="originalLanguage"
            rules={[
              { required: true, message: "La langue originale est requise" },
            ]}
          >
            <SelecteurLangue />
          </Form.Item>

          {/* Meme piege que dans le formulaire de modification : avec un second
              enfant, Form.Item ne relie pas la case au formulaire. */}

          {/* Classification par âge (CMP-03). Exigée par App Store et Play
              Console, et indispensable au futur profil enfant : sans elle,
              il n'y a rien à filtrer. */}
          <Form.Item
            label="Classification par âge"
            name="ageRating"
            tooltip="Âge minimum conseillé. Sans classification, la fiche du magasin doit être calée sur le titre le plus mature du catalogue."
            dependencies={["status"]}
            rules={[
              ({ getFieldValue }) => ({
                // Obligatoire seulement pour publier : un brouillon se
                // commence sans connaître la classification. Le serveur
                // applique la même règle — ceci ne fait que l'annoncer avant
                // l'envoi.
                required: getFieldValue("status") === "PUBLISHED",
                message:
                  "La classification est obligatoire pour publier. Choisissez un âge, ou enregistrez en brouillon.",
              }),
            ]}
          >
            <SelecteurAgeMinimum />
          </Form.Item>

          <Form.Item
            label="Motifs de la classification"
            name="contentDescriptors"
            tooltip="Violence, langage, contenu sexuel… Les magasins demandent les motifs en plus de l'âge."
          >
            <SelecteurDescripteurs />
          </Form.Item>

          <Form.Item
            name="hasSubtitles"
            valuePropName="checked"
            label="Sous-titres disponibles"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="hasMultiAudio"
            valuePropName="checked"
            label="Audio multilingue disponible"
          >
            <Switch />
          </Form.Item>
          {/* L'affiche est uploadée après la création du contenu */}
          <Form.Item
            label="Tags"
            name="tags"
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value.length === 0) return Promise.resolve();
                  const longTags = value.filter(
                    (tag: string) => tag.length > 50
                  );
                  if (longTags.length > 0) {
                    return Promise.reject(
                      new Error(
                        "Les tags ne peuvent pas dépasser 50 caractères chacun"
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              mode="tags"
              placeholder="Ajouter des tags"
              tokenSeparators={[","]}
            />
          </Form.Item>

          {selectedType &&
            (selectedType.title || "").toLowerCase() === "movie" && (
              <Card
                size="small"
                title="Informations Film"
                style={{ marginBottom: 16 }}
              >
                <Form.Item label="Durée (min)" name={["movie", "duration"]}>
                  <Input type="number" placeholder="120" min={1} />
                </Form.Item>
                <Form.Item
                  label="Réalisateur"
                  name={["movie", "director"]}
                  rules={[
                    {
                      max: 255,
                      message:
                        "Le nom du réalisateur ne peut pas dépasser 255 caractères",
                    },
                  ]}
                >
                  <Input
                    placeholder="Nom du réalisateur"
                    maxLength={255}
                    showCount
                  />
                </Form.Item>
                <Form.Item label="Budget (€)" name={["movie", "budget"]}>
                  <Input
                    type="number"
                    placeholder="50000000"
                    min={0}
                    addonAfter="€"
                  />
                </Form.Item>
                <Form.Item label="Box Office (€)" name={["movie", "boxOffice"]}>
                  <Input
                    type="number"
                    placeholder="200000000"
                    min={0}
                    addonAfter="€"
                  />
                </Form.Item>
              </Card>
            )}

          {selectedType &&
            (selectedType.title || "").toLowerCase() === "series" && (
              <Card
                size="small"
                title="Informations Série"
                style={{ marginBottom: 16 }}
              >
                <Form.Item
                  label="Nombre de saisons"
                  name={["series", "totalSeasons"]}
                >
                  <Input type="number" placeholder="3" min={1} />
                </Form.Item>
                <Form.Item
                  label="Nombre total d'épisodes"
                  name={["series", "totalEpisodes"]}
                >
                  <Input type="number" placeholder="24" min={1} />
                </Form.Item>
                <Form.Item
                  label="Durée moyenne par épisode (min)"
                  name={["series", "averageEpisodeDuration"]}
                >
                  <Input type="number" placeholder="45" min={1} />
                </Form.Item>
              </Card>
            )}

          {/* Composant de sélection du casting */}
          <Form.Item name="casts" style={{ marginBottom: 16 }}>
            <CastingSelector contentId={createdContent?.id} />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={loading}>
            Enregistrer le contenu
          </Button>
        </Form>
      </Card>

      {/* Section Upload Médias */}
      {createdContent && (
        <>
          <Card
            title={
              posterUploaded
                ? "Affiche (Poster) - Uploadee"
                : "Uploader l'affiche (Poster)"
            }
            style={{ marginBottom: 16 }}
          >
            <p style={{ color: '#666', marginBottom: 16 }}>
              Uploadez l'image de l'affiche du contenu (JPG, PNG)
            </p>
            <Upload
              listType="picture-card"
              maxCount={1}
              accept="image/*"
              beforeUpload={(file) => {
                setPosterFileList([file]);
                return false;
              }}
              onRemove={() => setPosterFileList([])}
              fileList={posterFileList}
            >
              {posterFileList.length === 0 && !posterUploaded && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
            <Button
              type="primary"
              onClick={onUploadPoster}
              disabled={!createdContent || posterFileList.length === 0 || posterUploaded}
              loading={uploadingPoster}
              style={{ marginTop: 12 }}
            >
              {posterUploaded ? "Affiche uploadee" : "Uploader l'affiche"}
            </Button>
          </Card>

          <Card
            title={trailerUploaded ? "Bande d'annonce (Trailer) - Uploade" : "Uploader la bande d'annonce (Trailer)"}
            style={{ marginBottom: 16 }}
          >
            <p style={{ color: '#666', marginBottom: 16 }}>
              Uploadez une courte vidéo de 2-3 minutes pour présenter votre contenu
            </p>
            <Upload {...trailerUploadProps} maxCount={1} accept="video/*">
              <Button icon={<UploadOutlined />}>Sélectionner la bande d'annonce</Button>
            </Upload>
            <Button
              type="primary"
              onClick={onUploadTrailer}
              disabled={!createdContent || trailerFileList.length === 0}
              loading={uploadingTrailer}
              style={{ marginTop: 12 }}
            >
              Uploader la bande d'annonce
            </Button>
            {uploadingTrailer && (
              <div style={{ marginTop: 16 }}>
                <Progress
                  percent={trailerUploadProgress}
                  status={trailerUploadProgress === 100 ? "success" : "active"}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
                <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                  Upload en cours... {trailerUploadProgress}%
                </p>
              </div>
            )}
          </Card>

          <Card
            title={videoUploaded ? "Video complete - Uploadee" : "Uploader la video complete"}
            style={{ marginBottom: 16 }}
          >
            <p style={{ color: '#666', marginBottom: 16 }}>
              Uploadez la vidéo complète du film (peut prendre plusieurs minutes selon la taille)
            </p>
            <Upload {...videoUploadProps} maxCount={1} accept="video/*">
              <Button icon={<UploadOutlined />}>Sélectionner la vidéo complète</Button>
            </Upload>
            <Button
              type="primary"
              onClick={onUploadVideo}
              disabled={!createdContent || videoFileList.length === 0}
              loading={uploading}
              style={{ marginTop: 12 }}
            >
              Uploader la vidéo complète
            </Button>
            {uploading && (
              <div style={{ marginTop: 16 }}>
                <Progress
                  percent={videoUploadProgress}
                  status={videoUploadProgress === 100 ? "success" : "active"}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
                <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                  Upload en cours... {videoUploadProgress}% - Cela peut prendre plusieurs minutes selon la taille du fichier.
                </p>
              </div>
            )}
          </Card>
        </>
      )}

      {/* Statut du transcodage temps réel */}
      {processingStatus && (trailerUploaded || videoUploaded) && (
        <Card title="Statut du transcodage" style={{ marginBottom: 16 }}>
          {trailerUploaded && (
            <div style={{ marginBottom: 16 }}>
              <ProcessingStatusBadge
                status={processingStatus.trailerProcessingStatus}
                progress={processingStatus.trailerProcessingProgress}
                stage={processingStatus.trailerProcessingStage}
                type="trailer"
              />
            </div>
          )}
          {videoUploaded && (
            <div>
              <ProcessingStatusBadge
                status={processingStatus.videoProcessingStatus}
                progress={processingStatus.videoProcessingProgress}
                stage={processingStatus.videoProcessingStage}
                type="video"
              />
            </div>
          )}
        </Card>
      )}

      {!createdContent && (
        <Card title="Upload de videos">
          <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
            Creez d'abord le contenu ci-dessus pour pouvoir uploader les videos
          </p>
        </Card>
      )}
    </div>
  );
};

export default ContentCreate;
